/**
 * /pomodoro/:characterId — 番茄鐘陪讀 mini-app.
 *
 * Fan picks a character to study with. A 25-minute timer counts
 * down; at the 5 / 10 / 15 / 20 minute marks the character sends
 * a text encouragement. Reaching 25 min lets the fan tap "完成"
 * which calls the backend to credit +5 affinity (subject to streak
 * multiplier).
 *
 * Server-side anti-replay: start records a sessionId tied to a
 * server-side timestamp; complete validates elapsed ≥ 25 min and
 * marks the row completedAt atomically.
 *
 * If the fan navigates away or refreshes, the in-memory timer is
 * lost (v1 simplicity). The session remains valid on the backend
 * for 24h — they can reopen the route and the timer restarts from
 * scratch BUT they can still complete by tapping 完成 directly
 * (the server checks elapsed time, not the client's local timer).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getFanCharacter,
  startPomodoro,
  completePomodoro,
  type FanCharacterCard,
  type PomodoroStartResponse,
  type PomodoroCompleteResponse,
} from '../api/fan-api';
import { useDocumentTitle } from '../lib/useDocumentTitle';

const SESSION_DURATION_SEC = 25 * 60;

// Encouragement templates fired at the 5 / 10 / 15 / 20 minute
// marks. {name} interpolated from the character record. Future
// iters can swap these for personality-aware LLM-generated lines
// or TTS-spoken voice clips.
const ENCOURAGEMENTS: ReadonlyArray<{ atSec: number; build: (name: string) => string }> = [
  { atSec: 5 * 60,  build: (n) => `${n}：開始的 5 分鐘最難，你做到了 ✨` },
  { atSec: 10 * 60, build: (n) => `${n}：再撐一下，我陪你 ☕️` },
  { atSec: 15 * 60, build: (n) => `${n}：已經過一半囉，做得很好！` },
  { atSec: 20 * 60, build: (n) => `${n}：最後 5 分鐘，一起衝完！` },
];

type Phase =
  | { kind: 'loading' }
  | { kind: 'idle' }
  | { kind: 'running'; session: PomodoroStartResponse; elapsedSec: number }
  | { kind: 'finished'; result: PomodoroCompleteResponse }
  | { kind: 'error'; message: string };

function fmtMMSS(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const mm = Math.floor(s / 60).toString().padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

export function Pomodoro() {
  const { characterId } = useParams<{ characterId: string }>();
  const navigate = useNavigate();
  useDocumentTitle('番茄鐘陪讀');
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [character, setCharacter] = useState<FanCharacterCard | null>(null);
  const [encouragements, setEncouragements] = useState<string[]>([]);
  const intervalRef = useRef<number | null>(null);

  // Load character so we can show name + thumbnail.
  useEffect(() => {
    if (!characterId) return;
    let alive = true;
    void getFanCharacter(characterId)
      .then((c) => { if (alive) { setCharacter(c); setPhase({ kind: 'idle' }); } })
      .catch((e) => { if (alive) setPhase({ kind: 'error', message: (e as Error).message }); });
    return () => { alive = false; };
  }, [characterId]);

  // Drive the tick during running.
  useEffect(() => {
    if (phase.kind !== 'running') return;
    const sessionStarted = new Date(phase.session.startedAt).getTime();
    intervalRef.current = window.setInterval(() => {
      const elapsedSec = Math.floor((Date.now() - sessionStarted) / 1000);
      setPhase((prev) => prev.kind === 'running' ? { ...prev, elapsedSec } : prev);
      // Fire encouragements as we cross each mark.
      setEncouragements((prev) => {
        const characterName = character?.name ?? '角色';
        const due = ENCOURAGEMENTS
          .filter((e) => e.atSec <= elapsedSec)
          .map((e) => e.build(characterName));
        // Only update if a new one arrived.
        return due.length > prev.length ? due : prev;
      });
    }, 1000);
    return () => {
      if (intervalRef.current !== null) window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [phase.kind, phase.kind === 'running' ? phase.session.startedAt : '', character?.name]);

  const onStart = useCallback(async () => {
    if (!characterId) return;
    try {
      const session = await startPomodoro(characterId);
      setEncouragements([]);
      setPhase({ kind: 'running', session, elapsedSec: 0 });
    } catch (e) {
      setPhase({ kind: 'error', message: (e as Error).message });
    }
  }, [characterId]);

  const onComplete = useCallback(async () => {
    if (phase.kind !== 'running') return;
    try {
      const result = await completePomodoro(phase.session.sessionId);
      setPhase({ kind: 'finished', result });
    } catch (e) {
      setPhase({ kind: 'error', message: (e as Error).message });
    }
  }, [phase]);

  const isLoading = phase.kind === 'loading';
  const remainingSec = phase.kind === 'running'
    ? Math.max(0, SESSION_DURATION_SEC - phase.elapsedSec)
    : SESSION_DURATION_SEC;
  const canComplete = phase.kind === 'running' && phase.elapsedSec >= SESSION_DURATION_SEC;
  const progressPct = phase.kind === 'running'
    ? Math.min(100, (phase.elapsedSec / SESSION_DURATION_SEC) * 100)
    : 0;

  // Neon blue → violet sweep across the filled arc; the un-filled
  // remainder is a soft slate so the ring's silhouette stays legible
  // even at 0%. A box-shadow halo + slight pulse (while running) sells
  // the "AI focus is engaged" vibe — disabled when reduced-motion is
  // on so the ring stays steady.
  const ringStyle = useMemo<React.CSSProperties>(() => ({
    background: `conic-gradient(
      rgb(96 165 250) 0%,
      rgb(167 139 250) ${Math.max(progressPct, 0.01)}%,
      rgba(30, 41, 59, 0.85) ${progressPct}%,
      rgba(30, 41, 59, 0.85) 100%
    )`,
    boxShadow: phase.kind === 'running'
      ? '0 0 32px rgba(96,165,250,0.32), 0 0 64px rgba(167,139,250,0.18)'
      : phase.kind === 'finished'
        ? '0 0 40px rgba(96,165,250,0.45), 0 0 80px rgba(167,139,250,0.28)'
        : '0 0 22px rgba(96,165,250,0.18)',
    transition: 'box-shadow 320ms ease',
  }), [progressPct, phase.kind]);

  if (!characterId) return null;

  return (
    <div className="max-w-md mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
      <header className="flex items-center justify-between gap-2">
        <Link
          to={character ? `/relationships/${encodeURIComponent(character.id)}` : '/relationships'}
          className="text-slate-400 hover:text-slate-200 text-sm inline-flex items-center gap-1"
          aria-label="返回角色關係"
        >
          ← 返回
        </Link>
        <h1 className="text-lg sm:text-xl font-semibold">番茄鐘陪讀</h1>
        <span className="w-12" aria-hidden="true" />
      </header>

      {/* Character chip — skeleton shimmer while loading so the layout
          doesn't jump in once getFanCharacter resolves. */}
      {character ? (
        <div className="flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900 p-3">
          {character.thumbnailUrl ? (
            <img
              src={character.thumbnailUrl}
              alt=""
              className={
                'h-12 w-12 rounded-full object-cover ring-2 ring-transparent transition ' +
                (phase.kind === 'running' ? 'ring-blue-400/40 shadow-[0_0_18px_rgba(96,165,250,0.45)]' : '')
              }
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-slate-800 flex items-center justify-center text-slate-500">{character.name.slice(0, 1)}</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{character.name}</div>
            <div className="text-xs text-slate-500">陪你一起專注 25 分鐘</div>
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900 p-3">
          <div className="nx-skeleton-shimmer h-12 w-12 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="nx-skeleton-shimmer h-3 w-2/3 rounded" />
            <div className="nx-skeleton-shimmer h-2.5 w-1/2 rounded" />
          </div>
        </div>
      ) : null}

      {phase.kind === 'error' && (
        <div role="alert" className="rounded-2xl border border-rose-500/40 bg-rose-500/10 p-4 text-rose-200 text-sm">
          {phase.message}
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setPhase({ kind: 'idle' })}
              className="rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 text-sm"
            >重試</button>
          </div>
        </div>
      )}

      {/* Big timer ring — neon gradient conic-fill + outer halo. On
          finished phase, two staggered confirm-burst rings expand from
          the center so completion feels like a celebration not just a
          number flip. */}
      <div className="flex items-center justify-center py-2">
        <div className="relative h-56 w-56 rounded-full flex items-center justify-center" style={ringStyle}>
          <div className="absolute inset-2 rounded-full bg-slate-950 flex flex-col items-center justify-center">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
              {phase.kind === 'finished' ? '完成' : phase.kind === 'running' ? '剩餘' : '預計'}
            </div>
            <div className="text-5xl font-bold tabular-nums text-slate-100 mt-1">
              {phase.kind === 'finished' ? '🎉' : fmtMMSS(remainingSec)}
            </div>
            <div className="text-xs text-slate-500 mt-1">25:00</div>
            {phase.kind === 'finished' && (
              <>
                <span className="nx-confirm-ring nx-confirm-ring-1" />
                <span className="nx-confirm-ring nx-confirm-ring-2" />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      {(phase.kind === 'idle' || isLoading) && (
        <button
          type="button"
          onClick={() => { void onStart(); }}
          disabled={isLoading}
          className="w-full rounded-2xl bg-violet-500 hover:bg-violet-400 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium py-4 transition active:scale-[0.99]"
        >
          {isLoading ? '載入中…' : '開始 25 分鐘專注'}
        </button>
      )}
      {phase.kind === 'running' && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => { void onComplete(); }}
            disabled={!canComplete}
            className="w-full rounded-2xl bg-violet-500 hover:bg-violet-400 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium py-4 transition active:scale-[0.99]"
          >
            {canComplete ? '回報完成（+5 好感度）' : `還剩 ${fmtMMSS(remainingSec)}`}
          </button>
          <button
            type="button"
            onClick={() => setPhase({ kind: 'idle' })}
            className="w-full rounded-2xl border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-300 py-3 transition"
          >
            放棄這次（不計分）
          </button>
        </div>
      )}
      {phase.kind === 'finished' && (
        <div className="rounded-2xl border border-blue-500/40 bg-blue-500/10 p-5 space-y-3 text-center">
          <div className="text-blue-300 text-sm font-medium">陪讀完成 ✨</div>
          {phase.result.creditedDelta > 0 ? (
            <>
              <div className="text-3xl font-bold text-blue-200 tabular-nums">+{phase.result.creditedDelta}</div>
              <div className="text-xs text-slate-300">
                好感度 {phase.result.newValue !== null ? `已累積到 ${phase.result.newValue} / 100` : ''}
                {phase.result.streakDays && phase.result.streakDays >= 2 ? `（🔥 連續 ${phase.result.streakDays} 天）` : ''}
              </div>
              {phase.result.leveledUp && phase.result.newLevelName && (
                <div className="rounded-lg bg-violet-500/20 text-violet-200 text-sm font-medium py-2 px-3">
                  🎊 升級到「{phase.result.newLevelName}」！
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-amber-300">{phase.result.warning ?? '已完成，但好感度更新失敗，請稍後到關係頁查看。'}</div>
          )}
          <div className="flex flex-col gap-2 pt-2">
            <button
              type="button"
              onClick={() => navigate(`/relationships/${encodeURIComponent(characterId)}`)}
              className="w-full rounded-lg bg-violet-500 hover:bg-violet-400 text-white font-medium py-3 transition active:scale-[0.98]"
            >看看新的關係狀態</button>
            <button
              type="button"
              onClick={() => setPhase({ kind: 'idle' })}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-300 py-3 transition active:scale-[0.98]"
            >再來一輪</button>
          </div>
        </div>
      )}

      {/* Encouragement bubbles */}
      {encouragements.length > 0 && phase.kind === 'running' && (
        <ul className="space-y-2">
          {encouragements.map((msg, i) => (
            <li
              key={i}
              className="rounded-2xl bg-rose-500/15 border border-rose-500/30 text-rose-100 text-sm px-4 py-2 leading-relaxed"
            >
              💬 {msg}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
